import { CheckDouble, MoneyBag, ThumbsDown, ThumbsUp } from '@factorialco/f0-react/icons/app'
import { AttendanceCustomPeriodEmployeeItemFragment } from 'generated/resources/graphql'
import { AttendanceSidepanelCompensationBlockFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import type { TabId } from './types'

interface PrimaryAction {
  label: string
  icon: typeof ThumbsUp | typeof ThumbsDown | typeof CheckDouble | typeof MoneyBag
  onClick: () => void | Promise<void>
}

interface UsePrimaryActionParams {
  employee: AttendanceCustomPeriodEmployeeItemFragment
  approvePeriod: () => Promise<boolean>
  disapprovePeriod: () => Promise<boolean>
  activeTab: TabId
  selectedCompensationBlocks: AttendanceSidepanelCompensationBlockFragment[]
  openCompensationModal: () => void
}

export const usePrimaryAction = ({
  employee,
  approvePeriod,
  disapprovePeriod,
  activeTab,
  selectedCompensationBlocks,
  openCompensationModal,
}: UsePrimaryActionParams): PrimaryAction | undefined => {
  if (!employee._policies?.approveTimesheet.can) return undefined

  const isCompensationTab = activeTab === 'compensation'
  const hasSelectedCompensationBlocks = selectedCompensationBlocks.length > 0

  if (isCompensationTab) {
    if (!hasSelectedCompensationBlocks) return undefined

    if (employee.reviewStatus === 'pending') {
      return {
        label: i18n.t(
          'time_tracking.table_collection.employee_summary.actions.approve_and_compensate'
        ),
        icon: CheckDouble,
        onClick: async () => {
          const approved = await approvePeriod()
          if (approved !== false) {
            openCompensationModal()
          }
        },
      }
    }

    if (employee.reviewStatus === 'approved') {
      return {
        label: i18n.t('time_tracking.table_collection.employee_summary.actions.compensate'),
        icon: MoneyBag,
        onClick: () => {
          openCompensationModal()
        },
      }
    }

    return undefined
  }

  if (employee.reviewStatus === 'pending') {
    return {
      label: i18n.t('time_tracking.table_collection.employee_summary.actions.approve'),
      icon: ThumbsUp,
      onClick: async () => {
        await approvePeriod()
      },
    }
  }
  if (employee.reviewStatus === 'approved') {
    return {
      label: i18n.t('time_tracking.table_collection.employee_summary.actions.disapprove'),
      icon: ThumbsDown,
      onClick: async () => {
        await disapprovePeriod()
      },
    }
  }
  return undefined
}
